import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/core';

export interface DropdownPosition {
  top: number;
  left: number;
}

const FALLBACK_POSITION: DropdownPosition = { top: 50, left: 50 };
/* Waits one frame-ish so the token's DOM node exists before measuring. */
const TOKEN_RENDER_DELAY_MS = 10;
const DROPDOWN_GAP_PX = 5;

const findTokenElement = (
  editor: Editor | null,
  nodePos: number,
  element?: HTMLElement
): HTMLElement | undefined => {
  if (element) return element;
  if (!editor) return undefined;

  try {
    const dom = editor.view.domAtPos(nodePos);

    let tokenElement: HTMLElement;
    if (dom.node.nodeType === Node.TEXT_NODE) {
      tokenElement = (dom.node.parentElement || dom.node) as HTMLElement;
    } else {
      tokenElement = dom.node as HTMLElement;
    }

    if (tokenElement && !tokenElement.classList.contains('slot-chip')) {
      const slotChip = tokenElement.querySelector('.slot-chip') as HTMLElement;
      if (slotChip) {
        tokenElement = slotChip;
      }
    }

    return tokenElement;
  } catch {
    return undefined;
  }
};

const calculateDropdownPosition = (
  container: HTMLElement,
  tokenElement: HTMLElement
): DropdownPosition => {
  let offsetTop = 0;
  let offsetLeft = 0;
  let currentElement = tokenElement;

  while (
    currentElement &&
    currentElement !== container &&
    currentElement.offsetParent
  ) {
    offsetTop += currentElement.offsetTop;
    offsetLeft += currentElement.offsetLeft;
    currentElement = currentElement.offsetParent as HTMLElement;
  }

  return {
    top: offsetTop + tokenElement.offsetHeight + DROPDOWN_GAP_PX,
    left: offsetLeft
  };
};

export interface FloatingTokenDropdown {
  isOpen: boolean;
  position: DropdownPosition | null;
  selectedTokenPos: number | null;
  open: (nodePos: number, element?: HTMLElement) => void;
  close: () => void;
}

const useFloatingTokenDropdown = (
  editor: Editor | null,
  containerRef: RefObject<HTMLDivElement>
): FloatingTokenDropdown => {
  const [selectedTokenPos, setSelectedTokenPos] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const measureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => (): void => {
      if (measureTimeoutRef.current) clearTimeout(measureTimeoutRef.current);
    },
    []
  );

  const open = useCallback(
    (nodePos: number, element?: HTMLElement): void => {
      setSelectedTokenPos(nodePos);

      measureTimeoutRef.current = setTimeout(() => {
        const container = containerRef.current;
        if (!container) return;

        const tokenElement = findTokenElement(editor, nodePos, element);
        setPosition(
          tokenElement
            ? calculateDropdownPosition(container, tokenElement)
            : FALLBACK_POSITION
        );
        setIsOpen(true);
      }, TOKEN_RENDER_DELAY_MS);
    },
    [editor, containerRef]
  );

  const close = useCallback((): void => {
    setIsOpen(false);
    setPosition(null);
    setSelectedTokenPos(null);
  }, []);

  return { isOpen, position, selectedTokenPos, open, close };
};

export default useFloatingTokenDropdown;
